// src/pages/shop/ShopMarkingChecker.jsx
//
// Чекер маркировки. Склад — арбитр: каждый код резолвим в карточку товара
// (by-barcode умеет и штрихкод, и код маркировки) и показываем название.
// Особый случай ЧЗ↔ЧЗ: коды должны быть идентичны (та же наклейка), потому что
// у одной физической единицы обе наклейки несут один и тот же полный код.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { products as productsApi } from '../../api';
import ScanButton from '../../components/ScanButton';
import Icon from '../../components/Icon';
import { extractGtin, looksLikeDataMatrix, displayCode } from '../../utils/marking';

export default function ShopMarkingChecker() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [pA, setPA] = useState(null);       // товар по коду 1 (или null = нет на складе)
  const [pB, setPB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async (code) => {
    if (!code) return null;
    try {
      const r = await productsApi.byBarcode(code);
      return r?.product || null;
    } catch (e) { return null; }
  };

  // Резолвим каждый код отдельно — чтобы название появлялось сразу после скана
  useEffect(() => {
    if (!a) { setPA(null); setLoadingA(false); return; }
    let cancelled = false; setLoadingA(true);
    lookup(a).then(p => { if (!cancelled) { setPA(p); setLoadingA(false); } });
    return () => { cancelled = true; };
  }, [a]);

  useEffect(() => {
    if (!b) { setPB(null); setLoadingB(false); return; }
    let cancelled = false; setLoadingB(true);
    lookup(b).then(p => { if (!cancelled) { setPB(p); setLoadingB(false); } });
    return () => { cancelled = true; };
  }, [b]);

  // Вердикт — когда оба кода введены и резолв завершён
  useEffect(() => {
    if (!a || !b) { setResult(null); return; }
    if (loadingA || loadingB) return;
    const bothDM = looksLikeDataMatrix(a) && looksLikeDataMatrix(b);
    let kind;
    if (bothDM) {
      if (a.trim() === b.trim()) kind = 'match';
      else if (extractGtin(a) && extractGtin(a) === extractGtin(b)) kind = 'sameDiff';
      else kind = 'mismatch';
    } else {
      if (!pA || !pB) kind = 'notfound';
      else if (pA.id === pB.id) kind = 'match';
      else kind = 'mismatch';
    }
    setResult(kind);
  }, [a, b, pA, pB, loadingA, loadingB]);

  const reset = () => { setA(''); setB(''); setResult(null); };

  const STYLE = {
    match:    { color: 'var(--success)', soft: 'var(--success-soft)', icon: 'check' },
    mismatch: { color: 'var(--danger)',  soft: 'var(--danger-soft, #fde8e8)', icon: 'x' },
    sameDiff: { color: 'var(--warning, #b45309)', soft: 'var(--warning-soft, #fef3c7)', icon: 'info' },
    notfound: { color: 'var(--fg-muted)', soft: 'var(--bg-subtle)', icon: 'alert' },
  };

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <h1 className="page-title" style={{ flex: 1 }}>{t('marking.checkerTitle')}</h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 16 }}>{t('marking.checkerHint')}</p>

        <ScanSlot label={t('marking.code1')} value={a} product={pA} loading={loadingA}
          onScan={setA} onChange={setA} onClear={() => setA('')}
          placeholder={t('marking.scanCode')} notOnStock={t('marking.notOnStock')} t={t} />

        <ScanSlot label={t('marking.code2')} value={b} product={pB} loading={loadingB}
          onScan={setB} onChange={setB} onClear={() => setB('')}
          placeholder={t('marking.scanCode')} notOnStock={t('marking.notOnStock')} t={t} />

        {result && (() => {
          const s = STYLE[result];
          return (
            <div className="card" style={{ padding: 18, marginTop: 16, textAlign: 'center', borderLeft: `4px solid ${s.color}` }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: s.soft, color: s.color }}>
                <Icon name={s.icon} size={26} />
              </div>
              <p style={{ fontSize: 17, fontWeight: 800, color: s.color }}>{t(`marking.${result}`)}</p>
              <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 4 }}>{t(`marking.${result}Desc`)}</p>
            </div>
          );
        })()}

        {(a || b) && (
          <button className="btn btn-secondary" onClick={reset} style={{ width: '100%', marginTop: 12 }}>
            {t('marking.checkNext')}
          </button>
        )}

        <div style={{ height: 20 }} />
      </div>
    </div>
  );
}

function ScanSlot({ label, value, product, loading, onScan, onChange, onClear, placeholder, notOnStock, t }) {
  return (
    <div className="card" style={{ padding: 14, marginBottom: 10 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', display: 'block', marginBottom: 6 }}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="input" style={{ flex: 1 }} value={displayCode(value)}
          onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        {value
          ? <button className="btn btn-ghost" onClick={onClear} style={{ padding: '0 12px' }}><Icon name="x" size={16} /></button>
          : <ScanButton raw onScan={onScan} size="lg" />}
      </div>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          {!loading && (
            <Icon name={product ? 'check' : 'alert'} size={14}
              color={product ? 'var(--success)' : 'var(--danger)'} />
          )}
          <span style={{ fontSize: 14, fontWeight: 700, color: loading ? 'var(--fg-muted)' : (product ? 'var(--fg)' : 'var(--danger)') }}>
            {loading ? t('marking.checking') : (product ? product.name : notOnStock)}
          </span>
        </div>
      )}
    </div>
  );
}
